import { Building2Icon } from 'lucide-react'

import { Button } from '@/components/button/button'
import { Card, CardContent } from '@/components/card/card'
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel } from '@/components/form/form'
import { Input } from '@/components/input/input'
import { Label } from '@/components/label/label'
import { Switch } from '@/components/switch/switch'

import { UserRole } from '@/lib/queries/business/types'
import { cn } from '@/lib/utils'

import { useSettingsContent } from './hooks/use-settings-content'
import { PrivacyAndSecurity } from './privacy-and-security/privacy-and-security'
import { Members } from './ui/members/members'
import { Payments } from './ui/payments/payments'

export const SettingsContent = () => {
  const {
    form,
    userInputImageSrc,
    dataUser,
    dataCompany,
    errors,
    isDisabledSaveButton,
    handleSubmit,
    handleChangeFileInput,
  } = useSettingsContent()

  return (
    <>
      <Form {...form}>
        <div className="space-y-8">
          {/* Organization name */}
          <FormField
            control={form.control}
            rules={{ required: true }}
            name="companyName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Organization Name</FormLabel>
                <FormControl>
                  <Input className="placeholder:text-faint-foreground" placeholder="Lawfully" {...field} />
                </FormControl>
                <FormDescription className="text-[0.8rem]">This is your public display name.</FormDescription>
              </FormItem>
            )}
          />

          {/* Organization logo */}
          <FormField
            control={form.control}
            name="companyLogo"
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            render={({ field: { value, onChange, ...fieldProps } }) => (
              <FormItem>
                <FormLabel>Organization Logo</FormLabel>
                <FormControl>
                  <div className="space-y-2">
                    <Card className="flex h-[100px] w-[100px] items-center justify-center overflow-hidden rounded">
                      {userInputImageSrc ? (
                        <img className="h-auto w-[100px]" src={userInputImageSrc} alt="" />
                      ) : dataCompany?.logo_img ? (
                        <img className="h-auto w-[100px]" src={dataCompany.logo_img} alt="" />
                      ) : (
                        <Building2Icon />
                      )}
                    </Card>
                    {!userInputImageSrc && !dataCompany?.logo_img && (
                      <p className={cn('text-sm text-muted-foreground', 'self-end')}>
                        This image will be displayed as your company logo if you do not upload your own.
                      </p>
                    )}
                    <Input
                      {...fieldProps}
                      className="placeholder:text-faint-foreground"
                      type="file"
                      accept="image/jpeg,image/png"
                      errorMessage={errors.companyLogo?.message}
                      onChange={($event) => handleChangeFileInput($event, onChange)}
                    />
                  </div>
                </FormControl>
              </FormItem>
            )}
          />

          {/* User name */}
          <FormField
            control={form.control}
            rules={{ required: true }}
            name="userName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input className="placeholder:text-faint-foreground" placeholder="Your Name" {...field} />
                </FormControl>
              </FormItem>
            )}
          />

          {/* Email */}
          <div className="space-y-2">
            <Label>Email</Label>
            <div className="rounded border bg-faint px-3 py-2 text-sm text-muted-foreground">
              {dataUser?.user.email}
            </div>
            <div className="text-[0.8rem] text-muted-foreground">Email address cannot be changed.</div>
          </div>

          {/* Privacy & Security */}
          <PrivacyAndSecurity isSuperAdmin={dataUser?.user.role === UserRole.SUPER_ADMIN} />

          {/* Payments */}
          {dataUser?.user.isAdmin && <Payments user={dataUser} />}

          {/* Members */}
          {dataUser?.user.isAdmin && <Members user={dataUser} />}

          {/* General settings */}
          {dataUser?.user.isAdmin && (
            <div className={cn('space-y-2')}>
              <div className={cn('text-sm font-medium')}>General</div>
              <div className={cn('text-[0.8rem] text-muted-foreground')}>
                You can change the settings that apply to the entire organization.
              </div>
              <Card>
                <CardContent className={cn('p-4')}>
                  <FormField
                    control={form.control}
                    name="companyDefaultCaseVisibility"
                    render={({ field }) => (
                      <FormItem>
                        <div className={cn('flex items-center', 'w-full')}>
                          <FormLabel className={cn('mr-auto')}>
                            Newly added cases will be visible to all by default
                          </FormLabel>
                          <FormControl>
                            <Switch checked={field.value} onCheckedChange={field.onChange} />
                          </FormControl>
                        </div>
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
              <Card>
                <CardContent className={cn('p-4')}>
                  <FormField
                    control={form.control}
                    name="companyDefaultCaseInactivation"
                    render={({ field }) => (
                      <FormItem>
                        <div className={cn('flex items-center', 'w-full')}>
                          <FormLabel className={cn('mr-auto')}>
                            Completed cases (<i>Approved</i>, <i>Denied</i>, etc.) are inactivated after 60 days by
                            default
                          </FormLabel>
                          <FormControl>
                            <Switch checked={field.value} onCheckedChange={field.onChange} />
                          </FormControl>
                        </div>
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </div>
          )}

          <Button type="button" disabled={isDisabledSaveButton} onClick={handleSubmit}>
            Save changes
          </Button>
        </div>
      </Form>
    </>
  )
}
